'use client';
// Libs
import React, { useState, useCallback, useEffect } from 'react';
import { ReactElement } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Spinner, DialogCloseButton } from 'ui-components';
import Cropper from 'react-easy-crop';
import loadImage from 'blueimp-load-image';
// App
import { CropperDialogProps } from './cropper.dialog.interfaces';
import { getCroppedImage } from './canvas.utils';

import { CropperContainer, ButtonWithMargin, MirroredIcon, CropperActions } from './cropper.dialog.styles';

const CropperDialog = (props: CropperDialogProps): ReactElement => {
  const { onClose, file, aspect = 1 } = props;
  const [isOpen, setOpen] = useState(true);
  const [isCropping, setCropping] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [isImageSrcLoading, setImageSrcLoading] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  /**
   * Init component
   */
  useEffect(() => {
    initImageSrc();
  }, []);

  /**
   * Inits image source
   */
  const initImageSrc = async () => {
    if (file) {
      setImageSrcLoading(true);

      const data = await loadImage(file, {
        meta: true,
        orientation: true,
        canvas: true,
        pixelRatio: window.devicePixelRatio || 1,
        imageSmoothingEnabled: false,
      });

      if (data) {
        setImageSrc(data.image.toDataURL());
      }

      setImageSrcLoading(false);
    }
  };

  /**
   * Event fired on crop complete
   */
  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Rotates image by x°
   */
  const handleRotate = (degrees: number) => {
    setRotation(rotation + degrees);
  };

  /**
   * Center cropper on image
   */
  const handleCenterCrop = () => {
    setZoom(1);
  };

  /**
   * Fired on dialog close
   */
  const handleDialogClose = async (shouldCrop: boolean) => {
    let croppedImage: Blob = null;

    if (isImageSrcLoading) return;

    if (shouldCrop && imageSrc) {
      setCropping(true);

      croppedImage = await getCroppedImage(imageSrc, croppedAreaPixels, rotation, file.type);

      setCropping(false);
    }

    setOpen(false);
    onClose && onClose(croppedImage);
  };

  return (
    <Dialog open={isOpen} onClose={() => handleDialogClose(false)}>
      <DialogCloseButton onClick={() => handleDialogClose(false)} />
      <DialogTitle>Redimensionnez votre photo</DialogTitle>
      <DialogContent>
        <CropperContainer>
          {isImageSrcLoading && <Spinner />}
          <Cropper
            classes={{ cropAreaClassName: 'cropArea' }}
            image={imageSrc}
            crop={crop}
            rotation={rotation}
            zoom={zoom}
            minZoom={0.2}
            aspect={aspect}
            restrictPosition={zoom >= 1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </CropperContainer>
        <CropperActions>
          <ButtonWithMargin
            size="small"
            variant="outlined"
            color="tertiary"
            onClick={() => handleRotate(90)}
            startIcon={<MirroredIcon name="rotate-left" size="medium" color="textContrast" />}
          >
            Tourner
          </ButtonWithMargin>
          {(zoom < 1 && (
            <ButtonWithMargin
              size="small"
              variant="outlined"
              color="tertiary"
              onClick={() => handleCenterCrop()}
              startIcon={<MirroredIcon name="center-focus" size="medium" color="textContrast" />}
            >
              Ajuster
            </ButtonWithMargin>
          )) ||
            null}
        </CropperActions>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleDialogClose(false)} variant="outlined" color="tertiary">
          Annuler
        </Button>
        <Button
          onClick={() => handleDialogClose(true)}
          variant="contained"
          color="tertiary"
          startIcon={(isCropping && <Spinner color="contrast" />) || null}
        >
          Appliquer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CropperDialog;
